import { ReactNode, useEffect, useState } from "react";
import { useIsomorphicLayoutEffect } from "react-use";
import styled from "@emotion/styled";
import {
  createImpactShopBasketProduct,
  ImpactShopProductLink,
  ImpactShopProductQuickSelectOption,
  ImpactShopProductType,
  INITIAL_PRODUCT_STATE,
  locationCurrencyCodeMapper,
  ImpactShopProductState,
} from "@ecologi/shared";
import { ImpactShopProductPageImage } from "@ecologi/react-components/src/ImpactShopProductPage/ImpactShopProductPageImage";
import { ImpactShopProductPageSidebar } from "@ecologi/react-components/src/ImpactShopProductPage/ImpactShopProductPageSidebar";
import { ImpactShopProductPagePanel } from "@ecologi/react-components/src/ImpactShopProductPage/ImpactShopProductPagePanel";
import { ImpactShopProductPageCards } from "@ecologi/react-components/src/ImpactShopProductPage/ImpactShopProductPageCards";
import { ImpactShopProductPageBenefits } from "@ecologi/react-components/src/ImpactShopProductPage/ImpactShopProductPageBenefits";
import { Head } from "../../../components";
import { useFetchImpactShopStore } from "../../../store/impact-shop";
import { useStore } from "../../../store";
import {
  pushAddToCart,
  pushViewItem,
  toGA4Item,
  useFireOnce,
} from "../../../lib";
import { useAnalytics } from "../../../lib/analytics/useAnalytics";
import { ImpactShopHeader } from "@ecologi/react-components/src";
import { useImpactShopMessage } from "../hooks";
import { IMPACT_SHOP_MESSAGES } from "../constants";

const useImpactShopStore = useFetchImpactShopStore();

const Page = styled.div`
  display: grid;

  @media ${({ theme }) => theme.bp.desktopUp} {
    grid-template-columns: minmax(0, 1fr) 339px;
  }
  @media ${({ theme }) => theme.bp.wideDesktopUp} {
    grid-template-columns: minmax(0, 1fr) 383px;
  }
  @media ${({ theme }) => theme.bp.widerDesktopUp} {
    grid-template-columns: minmax(0, 1fr) 408px;
  }
  @media ${({ theme }) => theme.bp.extraWideDesktopUp} {
    grid-template-columns: minmax(0, 1fr) 430px;
  }
`;

const ProjectInfoContainer = styled(ImpactShopProductPagePanel)`
  background-color: ${({ theme }) => theme.colors.mistLight};
  padding-bottom: 20px;
  width: 100%;

  @media ${(props) => props.theme.bp.desktopUp} {
    background-color: ${({ theme }) => theme.colors.snowdrop};
  }
`;

const WhyMoreInfoContainer = styled(ImpactShopProductPagePanel)`
  background-color: ${({ theme }) => theme.colors.snowdrop};
  width: 100%;

  @media ${({ theme }) => theme.bp.desktopUp} {
    background-color: ${({ theme }) => theme.colors.mistLight};
  }
`;

const FundBreakdownList = styled.ul`
  line-height: 1.5;
  padding-left: 15px;
  margin-bottom: 24px;

  li {
    @counter-style tilde-list {
      system: cyclic;
      symbols: "~";
      suffix: " ";
    }

    list-style: tilde-list;
  }
`;

const StyledLink = styled.a`
  display: inline;
  color: ${({ theme }) => theme.colors.charcoal};
  text-decoration: underline;
`;

export type ImpactShopProductPageProps = {
  isLoggedIn: boolean;
  product: ImpactShopProductType;
};

export const ImpactShopProductPage = ({
  isLoggedIn,
  product,
}: ImpactShopProductPageProps) => {
  const { location } = useStore();

  const {
    addProductToBasket,
    basket,
    currency,
    getMessage,
    setCurrency,
    totalNumberOfProductsInBasket,
  } = useImpactShopStore();

  const [basketQty, setBasketQty] = useState(0);
  const [productState, setProductState] = useState<ImpactShopProductState>(
    INITIAL_PRODUCT_STATE
  );

  useEffect(() => {
    setCurrency(locationCurrencyCodeMapper[location.countryCode]);
  }, [location]);

  const { sendAnalyticsEvent } = useAnalytics();

  useFireOnce(
    () => {
      const item = toGA4Item(product, currency);
      pushViewItem(item, currency, item.price);
    },
    !!product && !!currency,
    [product?.slug, currency]
  );

  useIsomorphicLayoutEffect(() => {
    setBasketQty(totalNumberOfProductsInBasket());
  }, [basket]);

  const handleAddToBasket = (
    product: ImpactShopProductType,
    quantity: number,
    fundedBy: string,
    recipientEmail?: string
  ) => {
    if (quantity) {
      addProductToBasket(
        createImpactShopBasketProduct(
          product,
          quantity,
          fundedBy,
          recipientEmail
        )
      );

      if (getMessage() === IMPACT_SHOP_MESSAGES.BasketLimitExceeded) {
        sendAnalyticsEvent("impactShopBasketLimitExceeded", {
          productSlug: product.slug,
        });
        return;
      }

      const ga4Item = toGA4Item(product, currency, quantity);
      pushAddToCart([ga4Item], currency, ga4Item.price * quantity);

      setProductState(INITIAL_PRODUCT_STATE);

      sendAnalyticsEvent("impactShopAddToBasket", {
        productSlug: product.slug,
        quantity,
      });
    }
  };

  const handleQuickSelectClick = (
    option: ImpactShopProductQuickSelectOption
  ) => {
    sendAnalyticsEvent("impactShopQuickSelectOptionClick", {
      productSlug: product.slug,
      quickSelectOption: option.label,
    });
  };

  const handleSendGiftChange = (isChecked: boolean) => {
    sendAnalyticsEvent(
      isChecked ? "impactShopSendGiftChecked" : "impactShopSendGiftUnchecked",
      { productSlug: product.slug }
    );
  };

  const renderLink = (
    links: ImpactShopProductLink[],
    index: number
  ): ReactNode | null => {
    return links[index] ? (
      <StyledLink href={links[index].url} target="_blank" rel="noopener">
        {links[index].text}
      </StyledLink>
    ) : null;
  };

  useImpactShopMessage();

  return (
    <>
      <Head title={`${product.display.title} | Impact shop`} />
      <ImpactShopHeader
        pageTitle={product.display.title}
        basketItemCount={basketQty}
        isLoggedIn={isLoggedIn}
      />
      <Page>
        <ImpactShopProductPageImage
          title={product.display.title}
          image={product.display.images[0]}
          badgePath={product.display.badgePath}
        />
        <ImpactShopProductPageSidebar
          currency={currency}
          onAddToBasket={handleAddToBasket}
          onQuickSelectClick={handleQuickSelectClick}
          onSendGiftChange={handleSendGiftChange}
          product={product}
          productState={productState}
          setProductState={setProductState}
        />
        <ProjectInfoContainer
          fundBreakdown={
            product.display.fundBreakdown ? (
              <FundBreakdownList data-testid="fund-breakdown">
                {product.display.fundBreakdown.map((project) => (
                  <li key={project.text}>
                    {`${project.percentage}% `}
                    <StyledLink
                      href={project.path}
                      target="_blank"
                      rel="noopener"
                    >
                      {project.text}
                    </StyledLink>
                  </li>
                ))}
              </FundBreakdownList>
            ) : null
          }
          header="The impact you will be making"
          summaryText={product.display.summary}
          link={renderLink(product.display.links, 0)}
        >
          <ImpactShopProductPageCards projectInfo={product.display} />
        </ProjectInfoContainer>
        <WhyMoreInfoContainer
          fundBreakdown={null}
          header={product.display.whyMoreTitle}
          link={renderLink(product.display.links, 1)}
          summaryText={product.display.whyMoreBody}
        >
          <ImpactShopProductPageBenefits benefits={product.display.benefits} />
        </WhyMoreInfoContainer>
      </Page>
    </>
  );
};
